import { useQuery } from '@tanstack/react-query';

import { DASH } from '@/app/shared/constants/common';
import { Interval } from '@/app/shared/constants/interval';
import { QueryKeys } from '@/app/shared/constants/queryKeys';
import { MODE_BY_BASIS, RateBasis } from '@/app/shared/constants/vaults';
import { HASTRA_VAULT_ID } from '@/app/shared/constants/web3/constants';
import {
  formatVaultApy,
  formatVaultShare,
  formatVaultTvl,
} from '@/app/shared/lib/vaultFormatters';
import { getVaults } from '@/app/shared/services/api';
import { VaultApiItem } from '@/app/shared/services/api/types';

export type { RateBasis } from '@/app/shared/constants/vaults';
export { DASH } from '@/app/shared/constants/common';
export {
  formatApyLabel,
  formatVaultApy,
  formatVaultPercent,
  formatVaultShare,
  formatVaultTvl,
} from '@/app/shared/lib/vaultFormatters';

export interface VaultTableRow {
  id: string;
  name: string;
  asset: string;
  chain: string;
  curator: string;
  tvl: string;
  tvlBase: string;
  seniorShare: string;
  juniorShare: string;
  baseApy: string;
  seniorApy: string;
  juniorApy: string;
  isComingSoon?: boolean;
}

const comingSoonVault = (id: string): VaultTableRow => ({
  id,
  name: 'Market Name',
  asset: 'USDC',
  chain: 'Ethereum',
  curator: 'Tetration',
  tvl: '$0',
  tvlBase: '$0',
  seniorShare: '0% of vault',
  juniorShare: '0% of vault',
  baseApy: '0%',
  seniorApy: '0%',
  juniorApy: '0%',
  isComingSoon: true,
});

export const COMING_SOON_VAULTS: VaultTableRow[] = [
  comingSoonVault('coming-soon-1'),
  comingSoonVault('coming-soon-2'),
  comingSoonVault('coming-soon-3'),
];

const mapVaultToRow = (vault: VaultApiItem): VaultTableRow => ({
  id: vault.id,
  name: vault.name || DASH,
  asset:
    vault.underlyingToken?.symbol ||
    (vault.id.toLowerCase() === HASTRA_VAULT_ID ? 'wYLDS' : 'USDC'),
  chain: 'Ethereum',
  curator: 'Tetration',
  tvl: formatVaultTvl(vault.tvl),
  tvlBase: formatVaultTvl(vault.tvlBaseUsd ?? vault.tvlBase),
  seniorShare: formatVaultShare(vault.trancheSeniorPercentage),
  juniorShare: formatVaultShare(vault.trancheJuniorPercentage),
  baseApy: formatVaultApy(vault.baseApy),
  seniorApy: formatVaultApy(vault.trancheSeniorAPY),
  juniorApy: formatVaultApy(vault.trancheJuniorAPY),
});

export const useVaults = (basis: RateBasis = 'current') => {
  const mode = MODE_BY_BASIS[basis];

  const { data, isLoading, error } = useQuery({
    queryKey: [QueryKeys.VAULTS, mode],
    queryFn: () => getVaults(mode),
    staleTime: Interval.ONE_MIN,
    refetchInterval: Interval.ONE_MIN,
  });

  const vaults = (data ?? []).map(mapVaultToRow);

  return {
    data,
    vaults,
    isLoading,
    error,
  };
};

export const useVaultById = (id?: string) => {
  const { data, isLoading, error } = useQuery({
    queryKey: [QueryKeys.VAULTS, 'current'],
    queryFn: () => getVaults('current'),
    staleTime: Interval.ONE_MIN,
    refetchInterval: Interval.ONE_MIN,
    enabled: !!id,
  });

  const vault = data?.find((item) => item.id.toLowerCase() === id?.toLowerCase());

  return {
    vault,
    name: vault?.name,
    isLoading,
    error,
  };
};
